import {
	HubConnection,
	HubConnectionBuilder,
	HubConnectionState,
	LogLevel,
} from "@microsoft/signalr";

export type RealtimeTopicHandler = (topic: string) => void;

// The server only sends "Invalidate" for topics this connection joined.
const topicHandlers = new Map<string, Set<RealtimeTopicHandler>>();
// Topics the server currently has this connection in (as far as we know).
const joinedTopics = new Set<string>();

let connection: HubConnection | null = null;
let startPromise: Promise<void> | null = null;
let restartTimer: ReturnType<typeof setTimeout> | null = null;
let restartAttempt = 0;
let syncScheduled = false;
// Syncs run one after another so a Subscribe is never overtaken by an older Unsubscribe.
let syncChain: Promise<void> = Promise.resolve();

/** Topics are stored procedure names without brackets, e.g. "M2.GetHeats". */
export const normalizeRealtimeTopic = (topic: string) =>
	topic.replaceAll("[", "").replaceAll("]", "").trim();

const getConnection = () => {
	if (connection) return connection;

	connection = new HubConnectionBuilder()
		.withUrl("/ws")
		.withAutomaticReconnect()
		.configureLogging(LogLevel.Information)
		.build();

	connection.on("Invalidate", (topic: string) => {
		topicHandlers.get(topic)?.forEach((handler) => {
			handler(topic);
		});
	});

	// Group membership belongs to the old connection id, so join everything again.
	connection.onreconnected(() => {
		joinedTopics.clear();
		scheduleTopicSync();
	});

	// Automatic reconnect gives up after ~40s (e.g. during a deploy). Keep retrying while
	// anything is listening, since nothing else would restart the connection.
	connection.onclose((error) => {
		joinedTopics.clear();
		if (error) console.log("SignalR connection closed", error);
		scheduleRestart();
	});

	return connection;
};

const startConnection = () => {
	const currentConnection = getConnection();

	if (currentConnection.state !== HubConnectionState.Disconnected) {
		return Promise.resolve();
	}

	if (startPromise) return startPromise;

	startPromise = currentConnection
		.start()
		.then(() => {
			console.log("SignalR connection started");
			restartAttempt = 0;
			joinedTopics.clear();
			scheduleTopicSync();
		})
		.catch((err) => {
			console.log("Error while starting SignalR connection: " + err);
			scheduleRestart();
		})
		.finally(() => {
			startPromise = null;
		});

	return startPromise;
};

const scheduleRestart = () => {
	if (restartTimer || topicHandlers.size === 0) return;
	const delay = Math.min(30_000, 2_000 * 2 ** restartAttempt++);
	restartTimer = setTimeout(() => {
		restartTimer = null;
		void startConnection();
	}, delay);
};

/**
 * Brings the server's group membership in line with the topics that have listeners. Batched
 * to one microtask so mounting a page with many queries sends one Subscribe call.
 */
const scheduleTopicSync = () => {
	if (syncScheduled) return;
	syncScheduled = true;
	queueMicrotask(() => {
		syncScheduled = false;
		syncChain = syncChain.then(syncTopics);
	});
};

const syncTopics = async () => {
	const currentConnection = connection;
	if (currentConnection?.state !== HubConnectionState.Connected) return;

	const toJoin = [...topicHandlers.keys()].filter((t) => !joinedTopics.has(t));
	const toLeave = [...joinedTopics].filter((t) => !topicHandlers.has(t));

	toJoin.forEach((t) => joinedTopics.add(t));
	toLeave.forEach((t) => joinedTopics.delete(t));

	try {
		if (toJoin.length) await currentConnection.invoke("Subscribe", toJoin);
	} catch (err) {
		// Forget them so the next sync retries.
		toJoin.forEach((t) => joinedTopics.delete(t));
		console.log("Error subscribing to realtime topics: " + err);
	}
	try {
		if (toLeave.length) await currentConnection.invoke("Unsubscribe", toLeave);
	} catch (err) {
		console.log("Error unsubscribing from realtime topics: " + err);
	}
};

/** Calls `handler` whenever the server invalidates `topic`. Returns the unsubscribe function. */
export const subscribeMesfRealtimeTopic = (
	rawTopic: string,
	handler: RealtimeTopicHandler,
) => {
	const topic = normalizeRealtimeTopic(rawTopic);
	let handlers = topicHandlers.get(topic);
	if (!handlers) {
		handlers = new Set();
		topicHandlers.set(topic, handlers);
		scheduleTopicSync();
	}
	handlers.add(handler);
	void startConnection();

	return () => {
		const current = topicHandlers.get(topic);
		if (!current) return;
		current.delete(handler);
		if (current.size === 0) {
			topicHandlers.delete(topic);
			scheduleTopicSync();
		}
	};
};
